/**
 * Sortable Image Item
 * 
 * Individual draggable image card for the gallery with drag handle.
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Star, Check, Play, Video, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isVideoMedia } from './galleryMediaUtils';

export interface GalleryImage {
  name: string;
  image: string;  // file_url (kept as "image" for backward compat)
  alt_text: string;
  media_type?: "image" | "video";  // Default "image" if missing
  is_primary?: number;  // Legacy field (map to is_primary_image)
  is_primary_image?: number;  // 0/1
  is_primary_video?: number;  // 0/1
  idx: number;
}

interface SortableImageItemProps {
  image: GalleryImage;
  index: number;
  disabled?: boolean;
  onSetPrimary?: (imageName: string, isVideo?: boolean) => void;  // Add isVideo param
  showPrimaryControl?: boolean;
  showPrimaryVideoControl?: boolean;  // New prop
  isActive?: boolean;
  reorderActive?: boolean;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  isDeleteMode?: boolean;
  isRearranging?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (imageName: string) => void;
  onVideoClick?: (videoUrl: string) => void;  // New prop for video lightbox
  onReplace?: () => void;  // OPS-1554: replace this media item
}

export function SortableImageItem({ 
  image, 
  index, 
  disabled = false, 
  onSetPrimary, 
  showPrimaryControl = false, 
  showPrimaryVideoControl = false,  // Add this
  isActive = false, 
  reorderActive = false, 
  onTouchStart, 
  onTouchEnd,
  isDeleteMode = false,
  isRearranging = false,
  isSelected = false,
  onToggleSelection,
  onVideoClick,  // Add this
  onReplace,
}: SortableImageItemProps) {
  const isTouchDevice =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;

  const imageId = image?.name || `image-${index}`;
  
  // Defensive fallback: detect video by file extension if media_type is wrong
  const isVideo = image ? isVideoMedia(image.media_type, image.image) : false;
  const isPrimaryVideo = image?.is_primary_video === 1;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: imageId,
    disabled: disabled || isDeleteMode || !image, // Disable drag in delete mode / no image
  });

  if (!image) {
    return null;
  }

  // Only apply transition when NOT dragging for snappy feel
  // Use faster easing for more responsive feel
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging 
      ? 'none' 
      : transition || 'transform 250ms cubic-bezier(0.2, 0, 0, 1)',
  };

  // In delete mode, make the whole card clickable to toggle selection
  // In action modes (choose primary, rearrange), handle interactions
  // Otherwise, allow clicks to bubble up to open Gallery Actions modal
  const handleClick = (e: React.MouseEvent) => {
    // In delete mode, toggle selection
    if (isDeleteMode && onToggleSelection && !disabled) {
      e.stopPropagation();
      onToggleSelection(image.name);
      return;
    }
    // In choose primary mode, clicking on the image (not the star) should not open modal
    // The star icon has its own click handler that stops propagation
    if (showPrimaryControl) {
      e.stopPropagation();
      return;
    }
    // In choose primary video mode, clicking on the video should not open modal
    if (showPrimaryVideoControl) {
      e.stopPropagation();
      return;
    }
    // In rearrange mode, clicking should not open modal (drag handles interactions)
    if (isRearranging) {
      e.stopPropagation();
      return;
    }
    // For videos with replace enabled, the DropdownMenu handles interaction
    // For videos without replace, fall back to direct lightbox
    if (isVideo && onVideoClick && !onReplace && !isDeleteMode) {
      e.stopPropagation();
      onVideoClick(image.image);
      return;
    }
    // For images with replace enabled, the DropdownMenu handles interaction
    // Otherwise, let clicks bubble up to Card to open Gallery Actions modal
    // Don't stop propagation - allow the click to reach the Card
  };

  return (
    <div
      ref={setNodeRef}
      data-sortable-image
      style={isDeleteMode ? undefined : style}
      {...(!disabled && !isDeleteMode && isTouchDevice ? { ...attributes, ...listeners } : {})}
      onTouchStart={(_e) => {
        if (!disabled && !isDeleteMode && isTouchDevice && onTouchStart) {
          onTouchStart();
        }
      }}
      onTouchEnd={(_e) => {
        if (!disabled && !isDeleteMode && isTouchDevice && onTouchEnd) {
          onTouchEnd();
        }
      }}
      onClick={handleClick}
      className={cn(
        "relative aspect-square rounded overflow-hidden border-2 bg-muted/30",
        "border-border/40 hover:border-border/60",
        isDeleteMode && "cursor-pointer transition-all duration-200",
        isDeleteMode && isSelected && "border-primary ring-2 ring-primary/30 shadow-lg scale-[1.02]",
        isDeleteMode && !isSelected && "hover:border-primary/40 hover:ring-1 hover:ring-primary/10",
        !isDeleteMode && isDragging 
          ? "opacity-0 pointer-events-none transition-none" 
          : "transition-transform duration-200 ease-out opacity-100",
        disabled && "cursor-default",
        // Improve touch reliability: prevent browser gestures / scroll stealing the pointer
        !isDeleteMode && "touch-none select-none will-change-transform"
      )}
    >
      <div
        className={cn(
          "w-full h-full relative group",
          // Active item: iOS-style scale-up when pressed/holding (before overlay takes over)
          isActive && !isDragging && "scale-[1.06] sm:scale-[1.08] transition-transform duration-200 ease-out",
          // Non-active items wiggle while reorder is active
          !isActive && reorderActive && "animate-ios-wiggle",
        )}
        style={
          !isActive && reorderActive
            ? { animationDelay: `${(index % 5) * 40}ms` }
            : undefined
        }
    >
      {isVideo ? (
        <div className="relative w-full h-full bg-gray-900 dark:bg-gray-800 overflow-hidden group cursor-pointer">
          {/* Video thumbnail with first frame */}
          <video
            src={image.image}
            className="w-full h-full object-cover pointer-events-none"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => {
              // Seek to first frame for thumbnail
              const video = e.currentTarget;
              if (video.duration > 0) {
                video.currentTime = 0.1;
              }
            }}
          />
          {/* Play icon overlay - appears on hover */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-16 h-16 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-black/70 group-hover:scale-110 transition-all">
              <Play className="h-8 w-8 text-white ml-1" fill="currentColor" />
            </div>
          </div>
          {/* VIDEO badge (always visible) */}
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-md backdrop-blur-sm">
            VIDEO
          </div>
          {/* Primary video badge */}
          {isPrimaryVideo && (
            <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] px-2 py-1 rounded-full font-medium backdrop-blur-sm">
              PRIMARY VIDEO
            </div>
          )}
        </div>
      ) : (
      <img
        src={image.image}
        alt={image.alt_text || `Image ${index + 1}`}
        className="w-full h-full object-cover pointer-events-none"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
      )}
      
        {/* Drag Handle - Only show when in rearrange mode, not disabled, and not on touch device */}
        {isRearranging && !disabled && !isTouchDevice && (
        <div
          {...attributes}
          {...listeners}
            className="absolute top-2 left-2 p-1.5 rounded-xs bg-black/50 backdrop-blur-sm text-white/90 cursor-grab active:cursor-grabbing hover:bg-black/70 hover:text-white active:scale-95 transition-all duration-150 z-10 flex items-center justify-center"
          aria-label="Drag to reorder"
        >
            <GripVertical className="h-4 w-4" />
          </div>
        )}
        
        {/* Delete mode checkbox */}
        {isDeleteMode && (
          <div className="absolute top-3 left-3 z-20">
            <div
              className={cn(
                "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all shadow-lg",
                isSelected
                  ? "bg-primary border-primary scale-110"
                  : "bg-background/95 border-border/60 backdrop-blur-md hover:border-primary/50"
              )}
            >
              {isSelected && (
                <Check className="h-5 w-5 text-primary-foreground stroke-[3]" />
              )}
            </div>
          </div>
        )}

        {/* Primary indicator / control - hide in delete mode */}
        {!isDeleteMode && (
          <>
            {/* Passive indicator (normal mode) - only show on primary image */}
            {!isVideo && image.is_primary && !showPrimaryControl && (
              <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-primary/90 text-primary-foreground text-[11px] font-medium backdrop-blur-sm z-10">
                Primary
              </div>
            )}
            
            {/* Interactive star (choose-primary mode) - images only */}
            {showPrimaryControl && !isVideo && (
              <div 
                className={cn(
                  "absolute top-3 right-3 flex items-center justify-center p-2.5 rounded-sm backdrop-blur-sm transition-all z-10 touch-manipulation min-w-[44px] min-h-[44px]",
                  image.is_primary
                    ? "bg-primary/90 text-primary-foreground"
                    : "bg-black/60 text-white/80",
                  (!disabled || showPrimaryControl) && !image.is_primary && "hover:bg-black/80 hover:text-white active:scale-95 cursor-pointer",
                  disabled && !showPrimaryControl && "opacity-60"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  // Allow click when showPrimaryControl is true, even if drag is disabled
                  if ((!disabled || showPrimaryControl) && onSetPrimary && !image.is_primary) {
                    onSetPrimary(image.name);  // isVideo defaults to false
                  }
                }}
                title={image.is_primary ? "Primary image" : disabled ? "" : "Tap to set as primary"}
              >
                <Star className={cn("h-5 w-5", image.is_primary && "fill-current")} />
              </div>
            )}

            {/* Interactive video control (choose-primary-video mode) - videos only */}
            {showPrimaryVideoControl && isVideo && (
              <div 
                className={cn(
                  "absolute top-3 right-3 flex items-center justify-center p-2.5 rounded-sm backdrop-blur-sm transition-all z-10 touch-manipulation min-w-[44px] min-h-[44px]",
                  isPrimaryVideo
                    ? "bg-primary/90 text-primary-foreground"
                    : "bg-black/60 text-white/80",
                  (!disabled || showPrimaryVideoControl) && "hover:bg-black/80 hover:text-white active:scale-95 cursor-pointer",
                  disabled && !showPrimaryVideoControl && "opacity-60"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  // Allow click when showPrimaryVideoControl is true, even if drag is disabled
                  // Allow clicking even if already primary (to unset it, though current behavior is to set another)
                  if ((!disabled || showPrimaryVideoControl) && onSetPrimary) {
                    if (import.meta.env.DEV) {
                      console.log('[GalleryDebug] Video primary control clicked:', {
                        imageName: image.name,
                        isPrimaryVideo,
                        showPrimaryVideoControl,
                        disabled,
                      });
                    }
                    onSetPrimary(image.name, true);  // Pass true for isVideo
                  } else {
                    if (import.meta.env.DEV) {
                      console.warn('[GalleryDebug] Video primary control click blocked:', {
                        disabled,
                        showPrimaryVideoControl,
                        hasOnSetPrimary: !!onSetPrimary,
                      });
                    }
                  }
                }}
                title={isPrimaryVideo ? "Primary video (tap to change)" : disabled ? "" : "Tap to set as primary video"}
              >
                <Video className={cn("h-5 w-5", isPrimaryVideo && "fill-current")} />
              </div>
            )}
          </>
        )}
      
      {/* Replace button — OPS-1554 */}
        {!isDeleteMode && !isRearranging && !showPrimaryControl && !showPrimaryVideoControl && onReplace && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onReplace();
            }}
            className={cn(
              "absolute bottom-3 right-3 z-20",
              "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md",
              "bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium",
              "hover:bg-black/80 active:scale-95",
              "touch-manipulation min-w-[44px] min-h-[32px] justify-center",
              // On desktop: show on hover only. On touch: always visible (no hover state).
              isTouchDevice ? "opacity-100" : "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
            )}
            title={isVideo ? "Replace video" : "Replace image"}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Replace</span>
          </button>
        )}

      {/* Image Index */}
        <div className="absolute bottom-3 left-3 px-2 py-1 rounded-xs bg-black/60 backdrop-blur-sm text-white text-xs font-medium">
        {index + 1}
        </div>
      </div>
    </div>
  );
}
